import React, { useState } from "react";
import moment from "moment";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import unsplash from "../api/unsplash";
import useConsulenzaData, { consulenzaAuth } from "./useConsulenzaData";
import { decimalFieldSx } from "./consulenzaFields";
export const categorieExtra = {
  sicurezza: "Sicurezza",
  medicina: "Medicina",
  formazione: "Formazione",
  bandi: "Bandi",
};
const stati = {
  da_completare: "Da completare",
  da_fatturare: "Da fatturare",
  fatturato: "Fatturato",
  annullato: "Annullato",
};
const euro = (v) =>
  Number(v || 0).toLocaleString("it-IT", {
    style: "currency",
    currency: "EUR",
  });
const eligible = (user, area) =>
  area === "bandi" || Number(user[categorieExtra[area]]) === 1;
export default function ExtraClienti({ clienteId, riepilogo = false }) {
  const [mese, setMese] = useState(moment().format("YYYY-MM"));
  const [area, setArea] = useState("");
  const [tecnico, setTecnico] = useState("");
  const [mostraAnnullati, setMostraAnnullati] = useState(false);
  const endpoint = riepilogo
    ? `extraClienti/riepilogo?area=${encodeURIComponent(area)}&tecnico=${encodeURIComponent(tecnico)}`
    : "extraClienti";
  const list = useConsulenzaData(endpoint, clienteId, mese);
  const [form, setForm] = useState(null);
  const [action, setAction] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [invoice, setInvoice] = useState({
    DataFatturazione: moment().format("YYYY-MM-DD"),
    RiferimentoFattura: "",
  });
  const owners = (list.data?.responsabili || []).filter((u) =>
    eligible(u, form?.Area),
  );
  const oldOwner =
    form?.ID &&
    form.Responsabile &&
    !owners.some((u) => Number(u.ID) === Number(form.Responsabile))
      ? {
          ID: form.Responsabile,
          username: `${form.ResponsabileNome} (assegnazione precedente)`,
        }
      : null;
  const ownerOptions = oldOwner ? [...owners, oldOwner] : owners;
  const newExtra = () => {
    const category =
      list.data?.areaPredefinita ||
      Object.keys(categorieExtra).find((a) => list.data?.aree[a]) ||
      "sicurezza";
    const own = (list.data?.responsabili || []).find(
      (u) => Number(u.ID) === Number(list.data.userId) && eligible(u, category),
    );
    setError("");
    setForm({
      Area: category,
      Data: moment().format("YYYY-MM-DD"),
      Descrizione: "",
      Importo: "",
      Responsabile: own?.ID || "",
      PreventivoRif: "",
      Stato: "da_fatturare",
    });
  };
  const changeArea = (value) =>
    setForm((p) => ({
      ...p,
      Area: value,
      Responsabile: (list.data?.responsabili || []).some(
        (u) => Number(u.ID) === Number(p.Responsabile) && eligible(u, value),
      )
        ? p.Responsabile
        : "",
      ResponsabileNome: "",
    }));
  const save = async () => {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await unsplash[form.ID ? "put" : "post"](
        "extraClienti",
        {
          cliente: clienteId,
          id: form.ID,
          versione: form.Versione,
          extra: form,
        },
        consulenzaAuth(),
      );
      const selectedMonth = form.Data.slice(0, 7);
      setForm(null);
      if (selectedMonth !== mese) setMese(selectedMonth);
      else await list.reload();
      setNotice("Extra salvato.");
    } catch (e) {
      setError(e.response?.data?.message || "Impossibile salvare l’extra");
    } finally {
      setBusy(false);
    }
  };
  const changeState = async () => {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await unsplash.post(
        "extraClienti/stato",
        {
          cliente: clienteId,
          id: action.row.ID,
          versione: action.row.Versione,
          azione: action.name,
          ...(action.name === "fatturato" ? invoice : {}),
        },
        consulenzaAuth(),
      );
      setAction(null);
      await list.reload();
      setNotice("Stato dell’extra aggiornato.");
    } catch (e) {
      setError(e.response?.data?.message || "Impossibile aggiornare lo stato");
    } finally {
      setBusy(false);
    }
  };
  const ask = (row, name) => {
    setError("");
    setInvoice({
      DataFatturazione: moment().format("YYYY-MM-DD"),
      RiferimentoFattura: "",
    });
    setAction({ row, name });
  };
  const rows = (list.data?.righe || []).filter(
    (r) => mostraAnnullati || r.Stato !== "annullato",
  );
  return (
    <Stack spacing={2}>
      <Box
        sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}
      >
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          {riepilogo ? "Fatturazione extra" : "I miei extra"}
        </Typography>
        {!riepilogo && (
          <Button
            variant="contained"
            onClick={newExtra}
            disabled={!list.data || list.data.cliente.Cessato === 1 || busy}
          >
            Nuovo extra
          </Button>
        )}
      </Box>
      <Typography variant="body2" color="text.secondary">
        {riepilogo
          ? "Riepilogo delle sole aree abilitate. Il periodo si riferisce alla data della prestazione. Lo stato Fatturato si aggiorna manualmente e non emette una fattura."
          : "Qui trovi soltanto i tuoi inserimenti. Puoi scegliere la categoria dell’extra anche quando lo inserisci per conto di un’altra area."}
      </Typography>
      {list.error && <Alert severity="error">{list.error}</Alert>}
      {error && !form && !action && <Alert severity="error">{error}</Alert>}
      {notice && <Alert severity="success">{notice}</Alert>}
      {list.data && !list.data.cliente.CodiceAgo && (
        <Alert severity="warning">
          Codice Ago mancante: gli extra non fatturati restano da completare e
          sono esclusi dal totale pronto da fatturare.
        </Alert>
      )}
      <Box
        sx={{
          display: "flex",
          gap: 1.5,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <TextField
          size="small"
          type="month"
          label="Mese prestazione"
          value={mese}
          InputLabelProps={{ shrink: true }}
          onChange={(e) => e.target.value && setMese(e.target.value)}
        />
        {riepilogo && (
          <>
            <TextField
              select
              size="small"
              label="Categoria"
              value={area}
              SelectProps={{ native: true }}
              InputLabelProps={{ shrink: true }}
              onChange={(e) => {
                setArea(e.target.value);
                setTecnico("");
              }}
            >
              <option value="">Tutte le aree abilitate</option>
              {Object.entries(categorieExtra)
                .filter(([key]) => list.data?.aree[key])
                .map(([key, label]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
            </TextField>
            <TextField
              select
              size="small"
              label="Tecnico responsabile"
              value={tecnico}
              SelectProps={{ native: true }}
              InputLabelProps={{ shrink: true }}
              onChange={(e) => setTecnico(e.target.value)}
            >
              <option value="">Tutti</option>
              {(list.data?.responsabili || []).map((u) => (
                <option key={u.ID} value={u.ID}>
                  {u.username}
                </option>
              ))}
            </TextField>
          </>
        )}
        <FormControlLabel
          label="Mostra annullati"
          control={
            <Switch
              checked={mostraAnnullati}
              onChange={(e) => setMostraAnnullati(e.target.checked)}
            />
          }
        />
        <Button
          size="small"
          disabled={busy || list.loading}
          onClick={list.reload}
        >
          Aggiorna elenco
        </Button>
      </Box>
      {riepilogo && list.data && (
        <>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            {Object.entries(list.data.totali).map(([key, value]) => (
              <Chip
                key={key}
                variant="outlined"
                color={key === "da_completare" ? "warning" : "default"}
                label={`${stati[key]}: ${euro(value)}`}
              />
            ))}
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Totali pronti e fatturati, esclusi gli extra da completare e
            annullati:
          </Typography>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            {Object.entries(list.data.perArea).map(([key, value]) => (
              <Chip
                key={key}
                size="small"
                label={`${categorieExtra[key]}: ${euro(value)}`}
              />
            ))}
            {list.data.perTecnico.map((t) => (
              <Chip
                key={t.ID}
                size="small"
                variant="outlined"
                label={`${t.Nome}: ${euro(t.Importo)}`}
              />
            ))}
          </Stack>
        </>
      )}
      {list.loading && (
        <CircularProgress size={20} aria-label="Caricamento extra" />
      )}
      {list.data &&
        (!rows.length ? (
          <Typography color="text.secondary">
            Nessun extra nel periodo selezionato.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {[
                    "Data",
                    "Categoria",
                    "Descrizione",
                    "Responsabile",
                    ...(riepilogo ? ["Inserito da"] : []),
                    "Importo",
                    "Stato",
                    "Riferimenti",
                    "Azioni",
                  ].map((l) => (
                    <TableCell key={l}>{l}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow
                    key={r.ID}
                    sx={{ opacity: r.Stato === "annullato" ? 0.6 : 1 }}
                  >
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {moment(r.Data).format("DD/MM/YYYY")}
                    </TableCell>
                    <TableCell>{categorieExtra[r.Area]}</TableCell>
                    <TableCell sx={{ minWidth: 180, whiteSpace: "pre-wrap" }}>
                      {r.Descrizione}
                    </TableCell>
                    <TableCell>{r.ResponsabileNome}</TableCell>
                    {riepilogo && <TableCell>{r.AutoreNome}</TableCell>}
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {euro(r.Importo)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={stati[r.Stato]}
                        color={
                          r.Stato === "da_completare"
                            ? "warning"
                            : r.Stato === "fatturato"
                              ? "success"
                              : "default"
                        }
                      />
                    </TableCell>
                    <TableCell>
                      {r.PreventivoRif && (
                        <Typography variant="body2">
                          Preventivo: {r.PreventivoRif}
                        </Typography>
                      )}
                      {r.DataFatturazione && (
                        <Typography variant="body2">
                          Fatturato il{" "}
                          {moment(r.DataFatturazione).format("DD/MM/YYYY")}
                          {r.RiferimentoFattura
                            ? ` — ${r.RiferimentoFattura}`
                            : ""}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {!riepilogo &&
                        !["fatturato", "annullato"].includes(r.Stato) && (
                          <Stack direction="row" spacing={0.5}>
                            <Button
                              size="small"
                              disabled={busy}
                              onClick={() => {
                                setError("");
                                setForm({
                                  ...r,
                                  PreventivoRif: r.PreventivoRif || "",
                                });
                              }}
                            >
                              Modifica
                            </Button>
                            {r.Stato === "da_completare" &&
                              list.data.cliente.CodiceAgo && (
                                <Button
                                  size="small"
                                  disabled={busy}
                                  onClick={() => ask(r, "pronto")}
                                >
                                  Pronto
                                </Button>
                              )}
                            <Button
                              size="small"
                              color="error"
                              disabled={busy}
                              onClick={() => ask(r, "annulla")}
                            >
                              Annulla extra
                            </Button>
                          </Stack>
                        )}
                      {riepilogo && r.Stato === "da_fatturare" && (
                        <Button
                          size="small"
                          disabled={busy}
                          onClick={() => ask(r, "fatturato")}
                        >
                          Segna fatturato
                        </Button>
                      )}
                      {riepilogo && r.Stato === "fatturato" && (
                        <Button
                          size="small"
                          disabled={busy}
                          onClick={() => ask(r, "riapri")}
                        >
                          Riapri
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ))}
      <Dialog
        open={!!form}
        onClose={() => !busy && setForm(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{form?.ID ? "Modifica extra" : "Nuovo extra"}</DialogTitle>
        {form && (
          <>
            <DialogContent>
              <Grid container spacing={2} sx={{ pt: 1 }}>
                {error && (
                  <Grid item xs={12}>
                    <Alert severity="error">{error}</Alert>
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    select
                    label="Categoria extra"
                    value={form.Area}
                    SelectProps={{ native: true }}
                    onChange={(e) => changeArea(e.target.value)}
                  >
                    {Object.entries(categorieExtra).map(([key, label]) => (
                      <option key={key} value={key}>
                        {label}
                      </option>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    size="small"
                    type="date"
                    label="Data prestazione"
                    value={form.Data}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ max: moment().format("YYYY-MM-DD") }}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, Data: e.target.value }))
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    multiline
                    minRows={2}
                    size="small"
                    label="Descrizione extra"
                    value={form.Descrizione}
                    inputProps={{ maxLength: 4000 }}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, Descrizione: e.target.value }))
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    size="small"
                    type="number"
                    sx={decimalFieldSx}
                    label="Importo a corpo (€)"
                    value={form.Importo}
                    inputProps={{ min: 0, step: "0.01" }}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, Importo: e.target.value }))
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={ownerOptions}
                    value={
                      ownerOptions.find(
                        (u) => Number(u.ID) === Number(form.Responsabile),
                      ) || null
                    }
                    isOptionEqualToValue={(a, b) =>
                      Number(a.ID) === Number(b.ID)
                    }
                    getOptionLabel={(u) => u.username}
                    noOptionsText={
                      form.Area === "bandi"
                        ? "Nessun responsabile disponibile per Bandi"
                        : "Nessun responsabile disponibile"
                    }
                    onChange={(e, u) =>
                      setForm((p) => ({ ...p, Responsabile: u?.ID || "" }))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        required
                        size="small"
                        label="Responsabile extra"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Riferimento preventivo accettato"
                    value={form.PreventivoRif}
                    inputProps={{ maxLength: 200 }}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, PreventivoRif: e.target.value }))
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    select
                    label="Stato extra"
                    value={form.Stato}
                    SelectProps={{ native: true }}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, Stato: e.target.value }))
                    }
                  >
                    <option value="da_completare">Da completare</option>
                    <option value="da_fatturare">Da fatturare</option>
                  </TextField>
                </Grid>
                {!list.data?.cliente.CodiceAgo && (
                  <Grid item xs={12}>
                    <Alert severity="warning">
                      Codice Ago mancante: il salvataggio manterrà l’extra da
                      completare.
                    </Alert>
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button disabled={busy} onClick={() => setForm(null)}>
                Annulla
              </Button>
              <Button variant="contained" disabled={busy} onClick={save}>
                Salva extra
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
      <Dialog
        open={!!action}
        onClose={() => !busy && setAction(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {action?.name === "fatturato"
            ? "Segna extra come fatturato"
            : action?.name === "riapri"
              ? "Riapri extra"
              : action?.name === "pronto"
                ? "Extra pronto da fatturare"
                : "Annulla extra"}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            <Typography>
              {action?.row.Descrizione} — {euro(action?.row.Importo)}
            </Typography>
            <Typography variant="body2">
              {action?.name === "fatturato" || action?.name === "riapri"
                ? "L’operazione aggiorna soltanto lo stato registrato in Koala."
                : action?.name === "annulla"
                  ? "L’extra sarà escluso dai totali e resterà consultabile attivando Mostra annullati."
                  : "L’extra entrerà nel totale pronto da fatturare."}
            </Typography>
            {action?.name === "fatturato" && (
              <>
                <TextField
                  type="date"
                  size="small"
                  required
                  label="Data fatturazione"
                  value={invoice.DataFatturazione}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ max: moment().format("YYYY-MM-DD") }}
                  onChange={(e) =>
                    setInvoice((p) => ({
                      ...p,
                      DataFatturazione: e.target.value,
                    }))
                  }
                />
                <TextField
                  size="small"
                  label="Numero / riferimento fattura"
                  value={invoice.RiferimentoFattura}
                  inputProps={{ maxLength: 100 }}
                  onChange={(e) =>
                    setInvoice((p) => ({
                      ...p,
                      RiferimentoFattura: e.target.value,
                    }))
                  }
                />
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setAction(null)}>
            Indietro
          </Button>
          <Button variant="contained" disabled={busy} onClick={changeState}>
            Conferma
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
